import React, { useEffect, useState } from 'react';
import {
  Modal,
  Paper,
  Box,
  Typography,
  Button,
  Divider,
  IconButton,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import axios from 'axios';
import SimpleCustomTable from '../../../components/table/SimpleCustomTable';
import { SimpleColumn } from '../../../components/table/interface';
import { formatCurrency } from '../../../utils/format/currency';
import EmptyStateMessage from '../../../components/layouts/EmptyStateMessage';

interface Props {
  open: boolean;
  onClose: () => void;
  officeId: number;
  officeName: string;
  showAlert: (message: string, severity?: 'success' | 'info' | 'warning' | 'error') => void;
  showLoading: () => void;
  hideLoading: () => void;
}

const modalStyle = {
  position: 'absolute' as const,
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: '90%',
  maxWidth: 1000,
  height: 600,
  bgcolor: 'background.paper',
  borderRadius: 2,
  boxShadow: 24,
  p: 0,
  display: 'flex',
  flexDirection: 'column',
  overflow: 'hidden',
};

const ModalOfficeBox: React.FC<Props> = ({
  open,
  onClose,
  officeId,
  officeName,
  showAlert,
  showLoading,
  hideLoading,
}) => {
  const [data, setData] = useState<any[]>([]);

  useEffect(() => {
    if (!open || !officeId) {
      setData([]);
      return;
    }

    const fetchBoxDetails = async () => {
      setData([]);
      showLoading();
      try {
        const response = await axios.get(`/report/boxcollectbyoffice/${officeId}`);
        const list = response.data?.data;
        setData(Array.isArray(list) ? list : []);
      } catch (error) {
        console.error('Error al cargar detalles de cajas:', error);
        showAlert('No se pudieron cargar las cajas de la oficina', 'error');
        setData([]);
      } finally {
        hideLoading();
      }
    };

    void fetchBoxDetails();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, officeId]);

  const columns: SimpleColumn[] = [
    {
      field: 'collect',
      headerName: 'Cobro',
      minWidth: 200,
      wrapText: true,
      renderCell: ({ row }) => {
        const name = row.collectName as string | undefined;
        return <Typography variant="body2">{name || '-'}</Typography>;
      },
    },
    {
      field: 'balance',
      headerName: 'Saldo',
      align: 'right',
      minWidth: 160,
      renderCell: ({ row }) => (
        <Typography variant="body2">{formatCurrency(Number(row.balance ?? 0))}</Typography>
      ),
    },
  ];

  const rows = data.map((box) => ({
    id: box.id,
    type: box.typeBox?.name || 'Sin tipo',
    balance: box.balance,
    date: box.date,
    collectName: box.collect?.name,
  }));

  return (
    <Modal open={open} onClose={onClose}>
      <Paper sx={modalStyle}>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            p: 2,
            bgcolor: 'primary.main',
            color: 'white',
          }}
        >
          <Typography variant="h6">Detalle de Cajas</Typography>
          <IconButton onClick={onClose} sx={{ color: 'white' }} aria-label="cerrar">
            <CloseIcon />
          </IconButton>
        </Box>

        <Box sx={{ p: 3, overflowY: 'auto', flexGrow: 1 }}>
          <Typography variant="h6" fontWeight="bold" gutterBottom>
            Oficina: {officeName}
          </Typography>
          <Divider sx={{ my: 2 }} />
          <Box sx={{ minHeight: 300 }}>
            {rows.length > 0 ? (
              <SimpleCustomTable
                rows={rows}
                columns={columns}
                maxHeight={360}
                showBorders
                showCount
                exportCsv
                exportFileName={`cajas-oficina-${officeId}.csv`}
                wrapCellContent
              />
            ) : (
              <EmptyStateMessage
                message="No hay cajas de cobro para mostrar con los permisos actuales."
                align="center"
              />
            )}
          </Box>
        </Box>

        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            p: 2,
            borderTop: 1,
            borderColor: 'divider',
          }}
        >
          <Button variant="outlined" onClick={onClose}>
            Cerrar
          </Button>
        </Box>
      </Paper>
    </Modal>
  );
};

export default ModalOfficeBox;
